import { buildQueryString } from '@utils/helpers';
import { PasswordResetRequestBody } from '../../@types';
import {
  AnnouncementRequestBody,
  ResourceRequestBody,
  SendEmailRequestBody,
  StaffInviteBody,
  StudentInviteBody,
  StudentInviteManyBody,
  VerifyTokenRequestBody,
} from '../../@types/admin';
import { lmsRequest } from './lmsRequest';

// Chapter API
export const apiChapter = {
  create: (data: Chapter, msgOptions?: MsgOptions) =>
    lmsRequest<Chapter>('/tenant/chapter').post().body(data, true).auth().send(msgOptions),

  update: async (chapterId: uuid, data: Chapter, msgOptions?: MsgOptions) =>
    lmsRequest<Chapter>(`/tenant/chapter/${chapterId}`).put().body(data, true).auth().send(msgOptions),

  delete: (chapterId: uuid, msgOptions?: MsgOptions) =>
    lmsRequest<Chapter>(`/tenant/chapter/${chapterId}`).delete().auth().send(msgOptions),
};

export const apiTenant = {
  get: (subdomain: string, msgOptions?: MsgOptions) => lmsRequest<Tenant>(`/tenant/${subdomain}`).send(msgOptions),

  update: (
    subdomain: string,
    data: Partial<Omit<Tenant, 'id' | 'logo_uploaded' | 'appareance_changed'>>,
    msgOptions?: MsgOptions
  ) => lmsRequest<any>(`/tenant/${subdomain}`).auth().put().body(data, true).send(msgOptions),
};

export const apiUserManagement = {
  staff: {
    get: {
      all: (msgOptions?: MsgOptions) => lmsRequest<User[]>(`/tenant/staff/list`).auth().get().send(msgOptions),
    },
    invite: {
      single: (data: StaffInviteBody, msgOptions?: MsgOptions) =>
        lmsRequest(`/tenant/user/invite`).auth().post().body(data, true).send(msgOptions),

      resend: (email: string, msgOptions?: MsgOptions) =>
        lmsRequest(`/tenant/user/invite/resend`).auth().post().body({ email }, true).send(msgOptions),
    },
  },
  student: {},
  enable: (id: uuid, msgOptions?: MsgOptions) =>
    lmsRequest(`/tenant/user/enable`).auth().post().body({ id }, true).send(msgOptions),

  disable: (id: uuid, msgOptions?: MsgOptions) =>
    lmsRequest(`/tenant/user/disable`).auth().post().body({ id }, true).send(msgOptions),

  delete: (id: uuid, msgOptions?: MsgOptions) =>
    lmsRequest(`/tenant/user/remove`).auth().post().body({ id }, true).send(msgOptions),
};

export const enrolledStudent = {
  update: (data: EnrolledStudent, msgOptions?: MsgOptions) =>
    lmsRequest(`/tenant/enrolled-student/update`).auth().post().body(data, true).send(msgOptions),

  revokeInvite: (id: uuid, msgOptions?: MsgOptions) =>
    lmsRequest('/tenant/enrolled-student/invite/revoke').auth().post().body({ id }, true).send(msgOptions),

  acceptEnrollment: (course_id: uuid, group_id: uuid, msgOptions?: MsgOptions) =>
    lmsRequest('/tenant/enrolled-student/accept').auth().post().body({ course_id, group_id }, true).send(msgOptions),
};

export const apiAnnouncement = {
  get: {
    single: {},
    all: (data: { course_id?: uuid; group_id?: uuid }, msgOptions?: MsgOptions) => {
      const endpoint = '/tenant/announcement/all';
      const url = data.course_id || data.group_id ? buildQueryString(endpoint, { ...data }) : endpoint;
      return lmsRequest<Announcement[]>(url).auth().send(msgOptions);
    },
  },
  postNew: (data: AnnouncementRequestBody, msgOptions?: MsgOptions) =>
    lmsRequest<Announcement>('/tenant/announcement').auth().post().body(data, true).send(msgOptions),

  update: (data: Announcement, msgOptions?: MsgOptions) =>
    lmsRequest<Announcement>('/tenant/announcement').auth().put().body(data, true).send(msgOptions),

  delete: (id: uuid, msgOptions?: MsgOptions) =>
    lmsRequest<Announcement>('/tenant/announcement').auth().delete().body({ id }, true).send(msgOptions),
};

export const apiCourse = {
  get: {
    all: (token?: JwtToken, msgOptions?: MsgOptions) =>
      lmsRequest<Course[]>('/tenant/course/all').auth(token).send(msgOptions),

    single: (courseId: uuid, msgOptions?: MsgOptions) =>
      lmsRequest<Course>('/tenant/course/' + courseId)
        .auth()
        .send(msgOptions),
  },

  create: {
    single: (data: any, msgOptions?: MsgOptions) =>
      lmsRequest<Course>('/tenant/course').post().body(data, true).auth().send(msgOptions),
  },

  update: (courseId: uuid, data: Partial<Course>, msgOptions?: MsgOptions) =>
    lmsRequest<any>(`/tenant/course/${courseId}`).put().body(data, true).auth().send(msgOptions),

  duplicate: (courseId: uuid, data: Partial<Course>, msgOptions?: MsgOptions) =>
    lmsRequest<Course>(`/tenant/course/duplicate/${courseId}`).post().body(data, true).auth().send(msgOptions),
};

export const apiActivity = {
  get: {
    single: (activityId: uuid, token?: string, msgOptions?: MsgOptions) =>
      lmsRequest<Activity>(`/tenant/activity/${activityId}`).get().auth(token).send(msgOptions),
  },
  create: (data: Activity, msgOptions?: MsgOptions) =>
    lmsRequest<Activity>('/tenant/activity').post().body(data, true).auth().send(msgOptions),

  update: (activityId: uuid, data: Partial<Activity>, msgOptions?: MsgOptions) =>
    lmsRequest<Activity>(`/tenant/activity/${activityId}`).put().body(data, true).auth().send(msgOptions),

  delete: (activityId: uuid, msgOptions?: MsgOptions) =>
    lmsRequest(`/tenant/activity/${activityId}`).delete().auth().send(msgOptions),
};

// Category API
type CreateCategoryBody = Omit<Category, 'id' | 'tenant_id'>;
export const apiCategory = {
  get: {
    single: {},
    all: (msgOptions?: MsgOptions) => lmsRequest<Category[]>('/tenant/category').auth().send(msgOptions),
  },
  create: {
    single: (data: CreateCategoryBody, msgOptions?: MsgOptions) =>
      lmsRequest<Category>('/tenant/category').post().body(data, true).auth().send(msgOptions),
  },
};

export const generateCloudinaryUploadSignature = (params_to_sign: any, msgOptions?: MsgOptions) =>
  lmsRequest('/cloudinary/sign/upload').auth().post().body({ params_to_sign }, true).send(msgOptions);

export const apiResources = {
  get: {
    allFromActivity: (activityId: uuid, msgOptions?: MsgOptions): Promise<any> =>
      lmsRequest<Resource[]>(`/tenant/activity/${activityId}/resource`).auth().send(msgOptions),
  },

  update: (resourceId: uuid, data: any, msgOptions?: MsgOptions) =>
    lmsRequest<Resource>(`/tenant/resource/${resourceId}`).auth().put().body(data, true).send(msgOptions),

  postNewToActivity: (activityId: uuid, data: ResourceRequestBody, msgOptions?: MsgOptions) =>
    lmsRequest<Resource>(`/tenant/activity/${activityId}/resource`).auth().post().body(data, true).send(msgOptions),

  delete: (resourceId: uuid, msgOptions?: MsgOptions) =>
    lmsRequest(`/tenant/resource/${resourceId}`).auth().delete().send(msgOptions),
};

export const apiEvent = {
  get: {
    single: {},
    all: (data: { course_id?: uuid; group_id?: uuid }, msgOptions?: MsgOptions) => {
      const endpoint = '/tenant/event/all';
      const query = data.course_id || data.group_id ? buildQueryString(endpoint, { ...data }) : endpoint;
      return lmsRequest<LMSEvent[]>(query).auth().send(msgOptions);
    },
  },
  create: (data: Partial<LMSEvent>, msgOptions?: MsgOptions) =>
    lmsRequest<LMSEvent>('/tenant/event').post().body(data, true).auth().send(msgOptions),

  update: (data: Partial<LMSEvent>, msgOptions?: MsgOptions) =>
    lmsRequest<LMSEvent>(`/tenant/event/${data.id}`).patch().body(data, true).auth().send(msgOptions),

  delete: (eventId: uuid, msgOptions?: MsgOptions) =>
    lmsRequest<LMSEvent>(`/tenant/event/${eventId}`).delete().auth().send(msgOptions),
};

export const apiGroup = {
  get: {
    single: (groupId: uuid, msgOptions?: MsgOptions) =>
      lmsRequest<Group>('/tenant/group/' + groupId)
        .auth()
        .send(msgOptions),
  },
};

export const apiStudents = {
  invite: {
    single: (data: StudentInviteBody, msgOptions?: MsgOptions) =>
      lmsRequest('/tenant/enrolled-student/invite').auth().post().body(data, true).send(msgOptions),

    many: (data: StudentInviteManyBody, msgOptions?: MsgOptions) =>
      lmsRequest('/tenant/enrolled-student/invite/bulk').auth().post().body(data, true).send(msgOptions),

    resend: (data: any, msgOptions?: MsgOptions) =>
      lmsRequest('/tenant/enrolled-student/invite/resend').auth().post().body(data, true).send(msgOptions),
  },
  get: {
    all: (groupId: uuid, msgOptions?: MsgOptions) =>
      lmsRequest<EnrolledStudent[]>('/tenant/enrolled-student/group/' + groupId)
        .auth()
        .send(msgOptions),
  },
};

export const apiUsers = {
  get: {
    single: {},
    all: (query?, msgOptions?: MsgOptions) =>
      lmsRequest(!query ? `/users` : buildQueryString(`/users`, query))
        .auth()
        .send(msgOptions),
  },
};

export const apiQuiz = {
  get: (activityId: uuid, token?: string, msgOptions?: MsgOptions) =>
    lmsRequest<any>(`/tenant/quiz/${activityId}`).get().auth(token).send(msgOptions),
  // This route is used for verifying a students attempt count before entering a quiz,
  // and for getting student info for submitting quiz info
  getQuizStudentAndAttempts: (code: string, token?: string, msgOptions?: MsgOptions) =>
    lmsRequest<any>(`/tenant/quiz-student-and-attempts-from-url-code/${code}`).get().auth(token).send(msgOptions),
  getGradebookData: (courseId: uuid, token?: string, msgOptions?: MsgOptions) =>
    lmsRequest<any>(`/tenant/gradebook-data/${courseId}`).auth(token).send(msgOptions),
  // This route is mainly used for getting a students attempts in SSR
  getStudentAttemptsWithStudentId: (activityId: uuid, studentId: uuid, token?: string, msgOptions?: MsgOptions) =>
    lmsRequest<any>(`/tenant/student-quiz-attempts/${activityId}/student/${studentId}`)
      .get()
      .auth(token)
      .send(msgOptions),
  // This route is for a student when viewing a single submission
  getStudentQuizAttempt: (submissionId: uuid, token?: string, msgOptions?: MsgOptions) =>
    lmsRequest<any>(`/tenant/quiz-attempt/${submissionId}`).get().auth(token).send(msgOptions),
  update: {
    quiz: (quizId: uuid, data: any, msgOptions?: MsgOptions) =>
      lmsRequest<any>(`/tenant/quiz`)
        .auth()
        .put()
        .body({ ...data, id: quizId }, true)
        .send(msgOptions),
    quizMultipleChoiceQuestion: (data: any, msgOptions?: MsgOptions) =>
      lmsRequest<any>(`/tenant/quiz-multiple-choice-question/`).auth().put().body(data, true).send(msgOptions),
    quizQuestionOrder: (quiz_id: uuid, data: any, msgOptions?: MsgOptions) =>
      lmsRequest<any>(`/tenant/quiz-question-order/`)
        .auth()
        .put()
        .body({ ...data, quiz_id }, true)
        .send(msgOptions),
    quizScore: (scoreId: uuid, score: number, msgOptions?: MsgOptions) =>
      lmsRequest<any>(`/tenant/quiz-score/${scoreId}`).auth().put().body({ score }, true).send(msgOptions),
    manualQuizAttempt: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/manual-quiz-attempt').put().auth().body(data, true).send(msgOptions),
    quizPrompt: (data, promptId, msgOptions?: MsgOptions) =>
      lmsRequest<any>(`/tenant/quiz-prompt/${promptId}`).put().auth().body(data, true).send(msgOptions),
    shortAnswerQuestion: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/quiz-short-answer-question').put().auth().body(data, true).send(msgOptions),
  },
  create: {
    quizMultipleChoiceQuestion: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/quiz-multiple-choice-question').post().auth().body(data, true).send(msgOptions),
    promptMultipleChoiceQuestion: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/quiz-prompt-multiple-choice-question').post().auth().body(data, true).send(msgOptions),
    quizAttempt: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/quiz-attempt').post().auth().body(data, true).send(msgOptions),
    manualQuizAttempt: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/manual-quiz-attempt').post().auth().body(data, true).send(msgOptions),
    quizPrompt: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/quiz-prompt').post().auth().body(data, true).send(msgOptions),
    shortAnswerQuestion: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/quiz-short-answer-question').post().auth().body(data, true).send(msgOptions),
    promptShortAnswerQuestion: (data, msgOptions?: MsgOptions) =>
      lmsRequest<any>('/tenant/quiz-prompt-short-answer-question').post().auth().body(data, true).send(msgOptions),
  },
  delete: {
    quizQuestion: (quizQuestionId, msgOptions?: MsgOptions) =>
      lmsRequest<any>(`/tenant/quiz-question/${quizQuestionId}`).delete().auth().send(msgOptions),
    quizPromptQuestion: (promptQuestionId, msgOptions?: MsgOptions) =>
      lmsRequest<any>(`/tenant/quiz-prompt-question/${promptQuestionId}`).delete().auth().send(msgOptions),
  },
};

export const sendFeedbackEmail = (data: object, msgOptions?: MsgOptions) =>
  lmsRequest('/tenant/send-feedback').auth().post().body(data, true).send(msgOptions);

export const getS3SignedUploadUrl = (filename: string, mediaType: string, msgOptions?: MsgOptions) =>
  lmsRequest(`/s3/sign/upload`).auth().post().body({ filename, type: mediaType }, true).send(msgOptions);

export const resendEmail = (email: string, subdomain: string, msgOptions?: MsgOptions) =>
  lmsRequest('/confirm/resend').post().body({ email, subdomain }, true).send(msgOptions);

export const tenantUpload = (data: FormData, msgOptions?: MsgOptions) =>
  lmsRequest('/tenant/upload').noDefaultHeaders().auth().post().body(data).send(msgOptions);

export const getRoles = (msgOptions?: MsgOptions) => lmsRequest<Role[]>(`/tenant/roles`).auth().send(msgOptions);

export const sendResetPasswordEmail = (data: SendEmailRequestBody, msgOptions?: MsgOptions) =>
  lmsRequest('/forgot').post().body(data, true).send(msgOptions);

export const resetPassword = (data: PasswordResetRequestBody, msgOptions?: MsgOptions) =>
  lmsRequest('/forgot').put().body(data, true).send(msgOptions);

export const verifyToken = ({ token, endpoint, type }: VerifyTokenRequestBody, msgOptions?: MsgOptions) =>
  lmsRequest<User>(endpoint).post().body({ token, type }, true).send(msgOptions);

export const uploadUserPhoto = (data: FormData, msgOptions?: MsgOptions) =>
  lmsRequest<User>('/me/upload').noDefaultHeaders().auth().post().body(data).send(msgOptions);

export const uploadCloudinaryImage = (image: File, folder: string, msgOptions?: MsgOptions) => {
  const formData = new FormData();
  formData.append('uploadImage', image);
  formData.append('folder', folder);
  return lmsRequest<User>('/tenant/cloudinary-image').noDefaultHeaders().auth().post().body(formData).send(msgOptions);
};

export const memberUpdate = (data: Partial<User>, msgOptions?: MsgOptions) =>
  lmsRequest(`/tenant/staff/update`).auth().post().body(data, true).send(msgOptions);

export const getActiveMembers = (msgOptions?: MsgOptions) =>
  lmsRequest(`/tenant/staff/list/active`).auth().get().send(msgOptions);

export const signupStaff = (data: object, msgOptions?: MsgOptions) =>
  lmsRequest<User>('/tenant/user/invite/accept').post().body(data, true).send(msgOptions);
